import React from 'react';
import { Layout, Typography, List, Divider, Anchor, Card, Collapse } from 'antd';
import { Link } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;
const { Content } = Layout;
const { Panel } = Collapse;
const { Link: AnchorLink } = Anchor;

const HelpPage = () => {
  const sidebarNavigation = [
    { title: 'Estates', description: 'View, create, and manage all estates.', link: '/estates' },
    { title: 'Family Jewels', description: 'Track and manage key family assets.', link: '/family-jewels' },
    { title: 'Probate Docs', description: 'Upload and organize legal documents.', link: '/probate-docs' },
    { title: 'Calendar', description: 'Schedule and view important events.', link: '/calendar' },
    { title: 'Family Tree', description: 'Visualize and manage the family structure.', link: '/family-tree' },
  ];

  const quickLinks = [
    { title: 'Assets', description: 'Add, view, and manage estate assets.', link: '/assets' },
    { title: 'Liabilities', description: 'List and track liabilities.', link: '/liabilities' },
    { title: 'Cashflows', description: 'Analyze estate-related cash flows.', link: '/cashflows' },
    { title: 'Participants', description: 'Manage participants involved in estates.', link: '/participants' },
  ];

  const actions = [
    {
      title: 'Add a New Estate', steps: [
        'Go to the Estates section from the sidebar.',
        'Click the Add Estate button.',
        'Fill in the details and save.',
      ]
    },
    {
      title: 'Upload a Document', steps: [
        'Navigate to the Probate Docs section.',
        'Click the Upload Document button.',
        'Select a document, assign a category, and save.',
      ]
    },
    {
      title: 'Add an Asset', steps: [
        'Open the Assets section from the sidebar.',
        'Click the Add Asset button.',
        'Enter details and associate it with an estate.',
      ]
    },
    {
      title: 'Schedule an Event', steps: [
        'Go to the Calendar section.',
        'Click the Add Event button.',
        'Enter the details and save.',
      ]
    },
  ];

  const assetComputationSections = [
    {
      title: '1. Market Value',
      content: <>
        <Paragraph>If the asset has a portfolio:</Paragraph>
        <Text code>Market Value = Cost Basis × Current Quantity Owned</Text>
        <Paragraph>📌 Only applied if the asset is not a Financial Asset (i.e., not a Bond, Equity, Loan, etc.)</Paragraph>
      </>,
    },
    {
      title: '2. Current Value',
      content: <>
        <Paragraph>If the asset is not a financial asset (e.g. Real Estate):</Paragraph>
        <Text code>Current Value = Market Value</Text>
        <Paragraph>Otherwise:</Paragraph>
        <Text code>Current Value = Original Quantity Owned × (Prior Day Closing Price or Cost Basis)</Text>
        <Paragraph>Example: <Text type="secondary">100 × $50 = $5,000</Text></Paragraph>
      </>,
    },
    {
      title: '3. Estimated Value',
      content: <>
        <Text code>Estimated Value = Current Value × (1 - Discount to Market ÷ 100)</Text>
        <Paragraph>Example: <Text type="secondary">$5,000 × (1 - 20 ÷ 100) = $4,000</Text></Paragraph>
      </>,
    },
    {
      title: '4. Net of Lien',
      content: <>
        <Paragraph>If the asset has a lease:</Paragraph>
        <Text code>Net of Lien = Market Value - Liens</Text>
        <Paragraph>Example: <Text type="secondary">$5,400 - $1,000 = $4,400</Text></Paragraph>
      </>,
    },
    {
      title: '5. Internal Distribution Amount',
      content: <>
        <Paragraph>If the asset is internally distributed with cash:</Paragraph>
        <Text code>Internal Distribution Amount = Estimated Value × (1 - Family Discount ÷ 100)</Text>
        <Paragraph>Example: <Text type="secondary">$4,000 × (1 - 10 ÷ 100) = $3,600</Text></Paragraph>
        <Paragraph>If not, it's set to 0.</Paragraph>
      </>,
    },
    {
      title: '6. Realised External Sale',
      content: <>
        <Paragraph>Only applies if:</Paragraph>
        <Paragraph>- The asset was not a cashless internal distribution, AND</Paragraph>
        <Paragraph>- It was either sold OR internally distributed with cash</Paragraph>
        <Text code>Realised External Sale = Internal Distribution Amount + Cash Received</Text>
        <Paragraph>Example: <Text type="secondary">$3,600 + $500 (cash received) = $4,100</Text></Paragraph>
      </>,
    },
    {
      title: '7. Estate Value & Counted in Probate',
      content: <>
        <Paragraph>If asset is covered in will:</Paragraph>
        <Text code>Estate Value = Estimated Value</Text>
        <Text code>To Be Counted in Probate = 0</Text>
        <Paragraph>If not covered in will:</Paragraph>
        <Text code>To Be Counted in Probate = Estimated Value</Text>
      </>,
    },
    {
      title: '8. Jointly Owned Value',
      content: <>
        <Paragraph>If the asset is jointly owned:</Paragraph>
        <Text code>Jointly Owned Value = Market Value</Text>
      </>,
    },
    {
      title: '9. EPTL Exemption Amount',
      content: <>
        <Paragraph>If EPTLExemption is true:</Paragraph>
        <Text code>EPTL Exemption Amount = 0</Text>
        <Paragraph>If false:</Paragraph>
        <Text code>EPTL Exemption Amount = Estimated Value</Text>
      </>,
    },
    {
      title: '10. Residual EPTL Exemption',
      content: <>
        <Paragraph>If there's an estimated value:</Paragraph>
        <Text code>Residual EPTL Exemption = Estimated Value</Text>
        <Paragraph>Otherwise:</Paragraph>
        <Text code>Residual EPTL Exemption = 0</Text>
      </>,
    },
    {
      title: '11. Small Estate Value',
      content: <>
        <Paragraph>If the estate is marked as small:</Paragraph>
        <Text code>Small Estate Value = Estimated Value</Text>
        <Paragraph>Otherwise:</Paragraph>
        <Text code>Small Estate Value = 0</Text>
      </>,
    },
    {
      title: '12. Family Discounted Value',
      content: <>
        <Text code>Family Discounted Value = Estimated Value × (1 - Family Discount ÷ 100)</Text>
        <Paragraph>Example: <Text type="secondary">$4,000 × 0.90 = $3,600</Text></Paragraph>
      </>,
    },
    {
      title: '13. Percentage Away From Estimated Value',
      content: <>
        <Paragraph>If the asset was sold:</Paragraph>
        <Text code>% Away = ((Cash Received - Estimated Value) ÷ Estimated Value) × 100</Text>
        <Paragraph>Example: <Text type="secondary">(($3,000 - $4,000) ÷ $4,000) × 100 = -25%</Text></Paragraph>
        <Paragraph>Shows how much actual cash received differed from the estimated value.</Paragraph>
      </>,
    },
    {
      title: '14. Counted in Probate Value',
      content: <>
        <Paragraph>Same rule as before:</Paragraph>
        <Paragraph>If covered in will → <Text code>0</Text></Paragraph>
        <Paragraph>Else → <Text code>Estimated Value</Text></Paragraph>
      </>,
    },
    {
      title: '15. Estate Left To Be Distributed',
      content: <>
        <Paragraph>If the asset is marked as distributed:</Paragraph>
        <Text code>Estate Left To Be Distributed = Estimated Value</Text>
        <Paragraph>Otherwise:</Paragraph>
        <Text code>Estate Left To Be Distributed = 0</Text>
      </>,
    },
  ];

  return (
    <Content style={{ padding: '2rem', background: '#fff' }}>
      <div style={{ display: 'flex', gap: '2rem' }}>
        <div style={{ flex: 1 }}>
          <Title level={2}>Help Center</Title>
          <Paragraph>Welcome! Use the links on the right to jump to any section.</Paragraph>

          <Divider />
          <Title level={3} id="sidebar-navigation">Sidebar Navigation</Title>
          <List
            itemLayout="vertical"
            dataSource={sidebarNavigation}
            renderItem={({ title, description, link }) => (
              <List.Item>
                <Card type="inner" title={<Link to={link}>{title}</Link>}>
                  {description}
                </Card>
              </List.Item>
            )}
          />

          <Divider />
          <Title level={3} id="quick-links">Quick Links</Title>
          <List
            itemLayout="vertical"
            dataSource={quickLinks}
            renderItem={({ title, description, link }) => (
              <List.Item>
                <Card type="inner" title={<Link to={link}>{title}</Link>}>
                  {description}
                </Card>
              </List.Item>
            )}
          />

          <Divider />
          <Title level={3} id="common-tasks">How to Perform Common Tasks</Title>
          <Collapse accordion>
            {actions.map(({ title, steps }) => (
              <Panel header={title} key={title}>
                <List size="small" dataSource={steps} renderItem={(step) => <List.Item>{step}</List.Item>} />
              </Panel>
            ))}
          </Collapse>

          <Divider />
          <Title level={3} id="asset-computations">Asset Value Computations</Title>
          <Collapse accordion>
            {assetComputationSections.map(({ title, content }) => (
              <Panel header={title} key={title}>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{content}</Paragraph>
              </Panel>
            ))}
          </Collapse>

          <Divider />
          <Title level={3} id="faqs">FAQs</Title>
          <List
            itemLayout="vertical"
            dataSource={[
              { question: 'How do I reset my password?', answer: 'Go to the Account section, select "Change Password," and follow the instructions.' },
              { question: 'Can I delete an estate?', answer: 'Yes, go to the Estates section, select the estate, and choose the delete option.' },
              { question: 'How do I log out?', answer: 'Click the Logout option in the sidebar at the bottom.' },
            ]}
            renderItem={({ question, answer }) => (
              <List.Item>
                <Card type="inner" title={question}>{answer}</Card>
              </List.Item>
            )}
          />

          <Divider />
          <Title level={3} id="contact">Contact Support</Title>
          <Paragraph>
            If you have additional questions or face any issues, please contact our support team through the Help Center or email us at <Text strong>support@example.com</Text>.
          </Paragraph>
        </div>

        <div style={{ width: '260px' }}>
          <Anchor offsetTop={100} affix>
            <AnchorLink href="#sidebar-navigation" title="Sidebar Navigation" />
            <AnchorLink href="#quick-links" title="Quick Links" />
            <AnchorLink href="#common-tasks" title="Common Tasks" />
            <AnchorLink href="#asset-computations" title="Asset Computations" />
            <AnchorLink href="#faqs" title="FAQs" />
            <AnchorLink href="#contact" title="Contact Support" />
          </Anchor>
        </div>
      </div>
    </Content>
  );
};

export default HelpPage;
